// DossierPanels — Messages & Documents de l'espace client « Mon dossier » (v5.21.0).
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { T } from "./cabinetTheme";

const API = process.env.REACT_APP_BACKEND_URL;
const uHdr = () => ({ Authorization: `Bearer ${localStorage.getItem("civ_user_token")}` });
const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} Mo` : `${Math.max(1, Math.round(b / 1024))} Ko`);

// ── Noter le cabinet (après une consultation terminée — avis client réel) ───
export const DossierReview = ({ slug, cabinetName }) => {
  const [state, setState] = useState(null);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [author, setAuthor] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API}/api/v3/cabinet/${slug}/reviews/me`, { headers: uHdr() })
      .then((r) => setState(r.data)).catch(() => setState({ eligible: false }));
  }, [slug]);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/v3/cabinet/${slug}/reviews`, { rating, text, author }, { headers: uHdr() });
      setState({ eligible: true, already_reviewed: true, review: r.data.review });
      toast.success("Merci pour votre avis !");
    } catch (e) {
      toast.error(e.response?.data?.error === "already_reviewed" ? "Vous avez déjà donné votre avis" : "Échec de l'envoi");
    } finally { setBusy(false); }
  };

  if (!state || (!state.eligible && !state.already_reviewed)) return null;
  if (state.already_reviewed && state.review) {
    return (
      <div style={T.card} data-testid="dossier-review-done">
        <h2 style={T.h2}>Votre avis sur {cabinetName}</h2>
        <div style={{ color: "#F59E0B", fontSize: 15, letterSpacing: 2, margin: "6px 0 3px" }}>{"★".repeat(state.review.rating)}{"☆".repeat(5 - state.review.rating)}</div>
        {state.review.text && <p style={{ ...T.sub, fontSize: 12.5, margin: 0 }}>{state.review.text}</p>}
        <div style={{ ...T.sub, fontSize: 10.5, marginTop: 6 }}>✅ Avis vérifié AYA — publié sur la page du cabinet.</div>
      </div>
    );
  }
  return (
    <div style={{ ...T.card, border: "1.5px solid rgba(14,138,79,0.3)", background: "#F7FCF9" }} data-testid="dossier-review-form">
      <h2 style={T.h2}>Comment s'est passée votre consultation ?</h2>
      <p style={{ ...T.sub, fontSize: 11.5, margin: "3px 0 9px" }}>Votre avis vérifié sera publié sur la page de {cabinetName}.</p>
      <div style={{ display: "flex", gap: 5 }} data-testid="dossier-review-stars">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} onClick={() => setRating(n)} data-testid={`dossier-star-${n}`}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 26, padding: 0, color: n <= rating ? "#F59E0B" : "#D1D5DB", lineHeight: 1 }}>★</button>
        ))}
      </div>
      <input value={author} onChange={(e) => setAuthor(e.target.value)} placeholder="Votre nom affiché (ex: K. N'Guessan)" maxLength={40}
        style={{ ...T.input, marginTop: 10, borderRadius: 10, fontSize: 12.5 }} data-testid="dossier-review-author" />
      <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Partagez votre expérience (optionnel)…" rows={3} maxLength={400}
        style={{ ...T.input, marginTop: 8, borderRadius: 10, fontSize: 12.5, resize: "vertical", fontFamily: "inherit" }} data-testid="dossier-review-text" />
      <button style={{ ...T.btnGreen, marginTop: 10, padding: "10px 16px", opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={submit} data-testid="dossier-review-submit">
        {busy ? "Envoi…" : "Publier mon avis"}
      </button>
    </div>
  );
};

export const DossierMessages = ({ slug, cabinetName }) => {
  const [messages, setMessages] = useState(null);
  const [input, setInput] = useState("");
  const endRef = useRef(null);
  const load = () => axios.get(`${API}/api/v3/cabinet/${slug}/my/messages`, { headers: uHdr() })
    .then((r) => { setMessages(r.data.messages); setTimeout(() => endRef.current?.scrollIntoView(), 60); })
    .catch(() => setMessages([]));
  useEffect(() => { load(); const t = setInterval(load, 25000); return () => clearInterval(t); }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    const text = input.trim();
    if (!text) return;
    setInput("");
    try {
      const r = await axios.post(`${API}/api/v3/cabinet/${slug}/my/messages`, { text }, { headers: uHdr() });
      setMessages((p) => [...(p || []), r.data.message]);
      setTimeout(() => endRef.current?.scrollIntoView(), 60);
    } catch { toast.error("Envoi impossible"); }
  };

  return (
    <div style={{ ...T.card, display: "flex", flexDirection: "column", height: "58vh" }} data-testid="dossier-messages">
      <h2 style={{ ...T.h2, paddingBottom: 8, borderBottom: "1px solid #F3F4F6" }}>Messages — {cabinetName}</h2>
      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 7, padding: "10px 0" }}>
        {messages === null && <div style={T.sub}>Chargement…</div>}
        {messages?.length === 0 && <div style={T.sub}>Écrivez votre premier message au cabinet 👇</div>}
        {(messages || []).map((m) => (
          <div key={m.message_id} style={{
            maxWidth: "80%", padding: "8px 12px", fontSize: 12.5, lineHeight: 1.4,
            ...(m.from === "client"
              ? { alignSelf: "flex-end", background: "#065F46", color: "#fff", borderRadius: "12px 12px 4px 12px" }
              : { alignSelf: "flex-start", background: "#F3F4F6", color: "#111827", borderRadius: "12px 12px 12px 4px" }),
          }} data-testid={`dossier-msg-${m.from}`}>
            {m.text}
            <div style={{ fontSize: 9.5, opacity: 0.6, marginTop: 2 }}>{String(m.at).slice(11, 16)}</div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div style={{ display: "flex", gap: 7 }}>
        <input style={{ ...T.input, flex: 1 }} value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Écrire au cabinet…" data-testid="dossier-msg-input" />
        <button onClick={send} style={{ ...T.btnGreen, padding: "0 16px" }} data-testid="dossier-msg-send">Envoyer</button>
      </div>
    </div>
  );
};

export const DossierDocuments = ({ slug }) => {
  const [docs, setDocs] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const load = () => axios.get(`${API}/api/v3/cabinet/${slug}/my/documents`, { headers: uHdr() })
    .then((r) => setDocs(r.data.documents)).catch(() => setDocs([]));
  useEffect(() => { load(); }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1048576) { toast.error("Fichier trop volumineux (max 8 Mo)"); return; }
    setUploading(true);
    try {
      await axios.post(`${API}/api/v3/cabinet/${slug}/documents`, file, {
        headers: { ...uHdr(), "Content-Type": file.type || "application/octet-stream", "X-Filename": encodeURIComponent(file.name) },
      });
      toast.success("Document transmis au cabinet ✓");
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Envoi impossible");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const open = async (d) => {
    try {
      const r = await axios.get(`${API}/api/v3/cabinet/documents/${d.document_id}/download`, { headers: uHdr(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { toast.error("Ouverture impossible"); }
  };
  const remove = async (d) => {
    try { await axios.delete(`${API}/api/v3/cabinet/documents/${d.document_id}`, { headers: uHdr() }); load(); }
    catch { toast.error("Suppression impossible"); }
  };

  return (
    <div style={T.card} data-testid="dossier-documents">
      <h2 style={T.h2}>Documents</h2>
      <div style={{ ...T.sub, fontSize: 11.5, margin: "4px 0 8px" }}>Transmettez vos pièces au cabinet (contrat de bail, courriers…) — PDF, JPG, PNG · 8 Mo max.</div>
      <div style={{ display: "grid", gap: 0 }}>
        {(docs || []).map((d) => (
          <div key={d.document_id} style={{ display: "flex", gap: 10, alignItems: "center", padding: "9px 0", borderBottom: "1px solid #F3F4F6" }} data-testid={`dossier-doc-${d.document_id}`}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: "#FEE2E2", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>📄</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 800, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.original_filename}</div>
              <div style={{ ...T.sub, fontSize: 11 }}>{fmtSize(d.size)} · {String(d.at).slice(0, 10)}</div>
            </div>
            <button style={{ background: "none", border: "none", color: "#0E8A4F", fontSize: 11.5, fontWeight: 800, cursor: "pointer" }} onClick={() => open(d)} data-testid={`dossier-doc-open-${d.document_id}`}>Ouvrir</button>
            <button style={{ background: "none", border: "none", color: "#9CA3AF", fontSize: 11.5, cursor: "pointer" }} onClick={() => remove(d)} data-testid={`dossier-doc-delete-${d.document_id}`}>✕</button>
          </div>
        ))}
        {docs !== null && docs.length === 0 && <div style={{ ...T.sub, padding: "4px 0 8px" }}>Aucun document transmis.</div>}
      </div>
      <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" style={{ display: "none" }}
        onChange={(e) => upload(e.target.files?.[0])} data-testid="dossier-doc-input" />
      <button style={{ ...T.btnOutline, width: "100%", padding: "9px 0", fontSize: 12, marginTop: 8, opacity: uploading ? 0.6 : 1 }}
        disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="dossier-add-document">
        {uploading ? "Envoi en cours…" : "Ajouter un document"}
      </button>
    </div>
  );
};
